import clsx from 'clsx';
import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { IconDotsVertical } from '@tabler/icons-react';

import { useFormatText } from '@/hooks/useFormatText';
import { useSettingsStore } from '@/store/useSettingsStore';

interface IAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  isDanger?: boolean;
}

interface IActionDropdownProps {
  actions: IAction[];
  dataTestId?: string;
}

const CLOSE_ALL_DROPDOWNS = 'close-all-dropdowns';

const ActionDropdown: React.FC<IActionDropdownProps> = ({
  actions,
  dataTestId = 'action-dropdown',
}) => {
  const { formatText } = useFormatText();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isOpen) {
      // Close all other dropdowns first
      document.dispatchEvent(new CustomEvent(CLOSE_ALL_DROPDOWNS));

      // Use setTimeout to ensure other dropdowns close first
      setTimeout(() => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const scrollX = window.scrollX;
          const scrollY = window.scrollY;

          setPosition({
            top: rect.bottom + scrollY + 5,
            left: isRTL ? rect.right + scrollX - 40 : rect.left + scrollX - 80,
          });
        }
        setIsOpen(true);
      }, 0);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleCloseAll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      // Use setTimeout to add listeners after the current event loop
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      document.addEventListener(CLOSE_ALL_DROPDOWNS, handleCloseAll);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener(CLOSE_ALL_DROPDOWNS, handleCloseAll);
    };
  }, [isOpen]);

  return (
    <>
      <div
        ref={buttonRef}
        onClick={toggle}
        className="cursor-pointer p-1 rounded-full hover:bg-neutral-100 w-fit mx-auto"
        data-testid={`${dataTestId}-toggle`}
      >
        <IconDotsVertical size={20} color="#717784" />
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-9999 flex flex-col gap-1 bg-white border border-neutral-light-200 rounded-lg p-3 w-fit! min-w-28"
            data-testid={`${dataTestId}-menu`}
            style={{
              top: position.top,
              left: position.left,
              boxShadow:
                '0px 4px 8px 0px rgba(24, 27, 37, 0.1), 0px 2px 4px 0px rgba(24, 27, 37, 0.06)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((action) => (
              <button
                key={action.key}
                data-testid={`${dataTestId}-item-${action.key}`}
                className={clsx(
                  'px-1.5 py-1.5 text-base text-start hover:bg-neutral-50 w-full flex items-center gap-2 cursor-pointer rounded-lg transition-colors',
                  action.isDanger
                    ? 'text-error-secondary'
                    : 'text-neutral-primary',
                  action.key === 'restore' &&
                    'text-brand-default! hover:bg-brand-light-50!'
                )}
                onClick={() => {
                  action.onClick();
                  setIsOpen(false);
                }}
              >
                <span
                  className={clsx(
                    'flex items-center justify-center',
                    action.isDanger && 'text-error-secondary',
                    action.key === 'restore' && 'text-brand-default! '
                  )}
                >
                  {action.icon}
                </span>
                <span className="whitespace-nowrap">
                  {formatText(action.label)}
                </span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default ActionDropdown;
